import { Card, CardContent, CardHeader, CardTitle } from '@/common/ui/card'
import { Gamepad2, Trophy } from 'lucide-react'
import { formatNumber } from '@/common/lib/utils'
import type { GetPlayerDetailResponse } from '@/modules/manage-player/type'
import { Badge } from '@/common/ui/badge'
import { Separator } from '@/common/ui/separator'

interface PlayerGameStatisticsProps {
	data: GetPlayerDetailResponse
}

export default function PlayerGameStatistics({
	data,
}: PlayerGameStatisticsProps) {
	const { gameStatistics } = data

	if (!gameStatistics || !gameStatistics.summary) {
		return (
			<Card className="border-dashed">
				<CardContent className="p-12">
					<div className="flex flex-col items-center justify-center text-center">
						<div className="mb-4 rounded-full bg-muted p-6">
							<Gamepad2 className="h-12 w-12 text-muted-foreground" />
						</div>
						<h3 className="mb-2 text-lg font-semibold">
							ยังไม่มีข้อมูลการเล่นเกม
						</h3>
						<p className="text-sm text-muted-foreground">
							สมาชิกยังไม่มีประวัติการเล่นเกม
						</p>
					</div>
				</CardContent>
			</Card>
		)
	}

	const { topProviders = [], gameTypes = [], topGames = [] } = gameStatistics

	// Get top items
	const topProvider = topProviders.length > 0 ? topProviders[0] : null
	const topGameType = gameTypes.length > 0 ? gameTypes[0] : null
	const topGame = topGames.length > 0 ? topGames[0] : null

	return (
		<div className="space-y-6">
			{/* 3 SEPARATE SECTIONS */}
			<div className="grid gap-4 md:grid-cols-3">
				{/* 1. Top Provider */}
				{topProvider && (
					<Card className="flex flex-col border-2 border-primary/30">
						<CardHeader>
							<CardTitle className="text-base">🏆 ค่ายเกมอันดับ 1</CardTitle>
						</CardHeader>
						<CardContent className="flex-1">
							<div className="mb-4">
								<h4 className="mb-2 text-3xl font-bold">
									{topProvider.provider}
								</h4>
								<p className="text-base text-muted-foreground">
									เล่น {formatNumber(topProvider.totalPlayCount)} ครั้ง
								</p>
							</div>
							<Separator className="my-4" />
							<div className="space-y-3">
								<div className="rounded-lg bg-muted/50 p-3">
									<p className="mb-1 text-xs text-muted-foreground">เดิมพัน</p>
									<p className="text-xl font-bold">
										{formatNumber(topProvider.totalBetAmount)} ฿
									</p>
								</div>
								<div className="rounded-lg bg-muted/50 p-3">
									<p className="mb-1 text-xs text-muted-foreground">
										กำไร/ขาดทุน
									</p>
									<p
										className={`text-xl font-bold ${
											topProvider.totalProfit < 0
												? 'text-green-600'
												: 'text-red-600'
										}`}
									>
										{topProvider.totalProfit < 0 ? '+' : '-'}
										{formatNumber(Math.abs(topProvider.totalProfit))} ฿
									</p>
								</div>
							</div>
						</CardContent>
					</Card>
				)}

				{/* 2. Top Game Type */}
				{topGameType && (
					<Card className="flex flex-col border-2 border-blue-500/30">
						<CardHeader>
							<CardTitle className="text-base">🎮 ประเภทเกมอันดับ 1</CardTitle>
						</CardHeader>
						<CardContent className="flex-1">
							<div className="mb-4">
								<h4 className="mb-2 text-3xl font-bold">
									{topGameType.gameType}
								</h4>
								<p className="text-base text-muted-foreground">
									เล่น {formatNumber(topGameType.totalPlayCount)} ครั้ง
								</p>
							</div>
							<Separator className="my-4" />
							<div className="space-y-3">
								<div className="rounded-lg bg-muted/50 p-3">
									<p className="mb-1 text-xs text-muted-foreground">เดิมพัน</p>
									<p className="text-xl font-bold">
										{formatNumber(topGameType.totalBetAmount)} ฿
									</p>
								</div>
								<div className="rounded-lg bg-muted/50 p-3">
									<p className="mb-1 text-xs text-muted-foreground">
										กำไร/ขาดทุน
									</p>
									<p
										className={`text-xl font-bold ${
											topGameType.totalProfit < 0
												? 'text-green-600'
												: 'text-red-600'
										}`}
									>
										{topGameType.totalProfit < 0 ? '+' : '-'}
										{formatNumber(Math.abs(topGameType.totalProfit))} ฿
									</p>
								</div>
							</div>
						</CardContent>
					</Card>
				)}

				{/* 3. Top Game */}
				{topGame && (
					<Card className="flex flex-col border-2 border-purple-500/30">
						<CardHeader>
							<CardTitle className="text-base">⭐ เกมอันดับ 1</CardTitle>
						</CardHeader>
						<CardContent className="flex-1">
							<div className="mb-4">
								<h4 className="mb-3 line-clamp-2 min-h-[3.5rem] text-2xl font-bold">
									{topGame.gameName}
								</h4>
								<div className="flex items-center justify-between gap-2">
									<div className="flex flex-wrap items-center gap-2">
										<Badge variant="secondary" className="text-xs">
											{topGame.provider}
										</Badge>
										<Badge variant="outline" className="text-xs">
											{topGame.gameType}
										</Badge>
									</div>
									<p className="whitespace-nowrap text-base text-muted-foreground">
										เล่น {formatNumber(topGame.totalPlayCount)} ครั้ง
									</p>
								</div>
							</div>
							<Separator className="my-4" />
							<div className="space-y-3">
								<div className="rounded-lg bg-muted/50 p-3">
									<p className="mb-1 text-xs text-muted-foreground">เดิมพัน</p>
									<p className="text-xl font-bold">
										{formatNumber(topGame.totalBetAmount)} ฿
									</p>
								</div>
								<div className="rounded-lg bg-muted/50 p-3">
									<p className="mb-1 text-xs text-muted-foreground">
										กำไร/ขาดทุน
									</p>
									<p
										className={`text-xl font-bold ${
											topGame.totalProfit < 0
												? 'text-green-600'
												: 'text-red-600'
										}`}
									>
										{topGame.totalProfit < 0 ? '+' : '-'}
										{formatNumber(Math.abs(topGame.totalProfit))} ฿
									</p>
								</div>
							</div>
						</CardContent>
					</Card>
				)}
			</div>

			{/* All Providers List */}
			<Card>
				<CardHeader>
					<div className="flex items-center justify-between">
						<CardTitle className="flex items-center gap-2 text-lg">
							<Gamepad2 className="h-5 w-5 text-primary" />
							สถิติค่ายเกม
						</CardTitle>
						<Badge variant="secondary">Top 5</Badge>
					</div>
				</CardHeader>
				<CardContent>
					<div className="space-y-3">
						{topProviders.map((provider, index) => (
							<div
								key={provider.provider}
								className="flex items-center justify-between rounded-lg border p-4 transition-all hover:border-primary/50 hover:shadow-md"
							>
								<div className="flex items-center gap-3">
									<Badge
										variant={index === 0 ? 'default' : 'outline'}
										className="text-sm font-bold"
									>
										#{index + 1}
									</Badge>
									<div>
										<h4 className="font-bold">{provider.provider}</h4>
										<p className="text-xs text-muted-foreground">
											เล่น {formatNumber(provider.totalPlayCount)} ครั้ง
										</p>
									</div>
								</div>
								<div className="text-right">
									<p className="text-sm font-semibold">
										{formatNumber(provider.totalBetAmount)} ฿
									</p>
									<p
										className={`text-sm font-bold ${
											provider.totalProfit < 0
												? 'text-green-600'
												: 'text-red-600'
										}`}
									>
										{provider.totalProfit < 0 ? '+' : '-'}
										{formatNumber(Math.abs(provider.totalProfit))} ฿
									</p>
								</div>
							</div>
						))}
					</div>
				</CardContent>
			</Card>

			{/* Top Games List */}
			{topGames.length > 0 && (
				<Card>
					<CardHeader>
						<div className="flex items-center justify-between">
							<CardTitle className="flex items-center gap-2 text-lg">
								<Trophy className="h-5 w-5 text-primary" />
								เกมที่เล่นมากที่สุด
							</CardTitle>
							<Badge variant="outline">Top 10</Badge>
						</div>
					</CardHeader>
					<CardContent>
						<div className="space-y-2">
							{topGames.slice(0, 10).map((game, index) => {
								const getRankBadge = (rank: number) => {
									if (rank === 0)
										return (
											<div className="flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-br from-yellow-400 to-yellow-600 text-base font-bold text-white shadow-lg">
												1
											</div>
										)
									if (rank === 1)
										return (
											<div className="flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-br from-gray-300 to-gray-500 text-base font-bold text-white shadow-lg">
												2
											</div>
										)
									if (rank === 2)
										return (
											<div className="flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-br from-orange-400 to-orange-600 text-base font-bold text-white shadow-lg">
												3
											</div>
										)
									return (
										<div className="flex h-10 w-10 items-center justify-center rounded-full bg-muted text-base font-semibold text-muted-foreground">
											{rank + 1}
										</div>
									)
								}

								return (
									<div
										key={game.gameId}
										className={`group flex items-center gap-4 rounded-lg border-2 p-4 transition-all hover:border-primary/50 hover:shadow-md ${
											index < 3
												? 'bg-gradient-to-r from-muted/50 to-transparent'
												: 'bg-card'
										}`}
									>
										{getRankBadge(index)}

										<div className="min-w-0 flex-1">
											<h4 className="truncate text-base font-bold">
												{game.gameName}
											</h4>
											<div className="flex flex-wrap items-center gap-2 text-xs">
												<Badge variant="outline" className="text-xs">
													{game.provider}
												</Badge>
												<Badge variant="secondary" className="text-xs">
													{game.gameType}
												</Badge>
												<span className="text-muted-foreground">
													เล่น {formatNumber(game.totalPlayCount)} ครั้ง
												</span>
											</div>
										</div>

										<div className="min-w-[120px] space-y-1 text-right">
											<div className="flex items-center justify-end gap-1">
												<span className="text-xs text-muted-foreground">
													เดิมพัน:
												</span>
												<span className="text-sm font-bold">
													{formatNumber(game.totalBetAmount)} ฿
												</span>
											</div>
											<div className="flex items-center justify-end gap-1">
												<span className="text-xs text-muted-foreground">
													กำไร:
												</span>
												<span
													className={`text-base font-bold ${
														game.totalProfit < 0
															? 'text-green-600'
															: 'text-red-600'
													}`}
												>
													{game.totalProfit < 0 ? '+' : '-'}
													{formatNumber(Math.abs(game.totalProfit))} ฿
												</span>
											</div>
										</div>
									</div>
								)
							})}
						</div>
					</CardContent>
				</Card>
			)}
		</div>
	)
}
